import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Logga in — Fenny admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  validateSearch: (s: Record<string, unknown>): { next?: string } =>
    typeof s.next === "string" ? { next: s.next } : {},
  component: LoginPage,
});

/** Endast relativa sökvägar på samma domän godtas som återvändsadress. */
function safeNext(next: string | undefined): string | null {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.includes("\\")) return null;
  return next;
}

function LoginPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const goNext = () => {
    const target = safeNext(next);
    if (target) window.location.assign(target);
    else navigate({ to: "/admin" });
  };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) goNext();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      await router.invalidate();
      goNext();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Något gick fel");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <form onSubmit={onSubmit} className="w-full max-w-sm space-y-4 rounded-2xl border border-border bg-card p-8">
        <h1 className="text-2xl font-semibold text-foreground">Admin · logga in</h1>
        <p className="text-sm text-muted-foreground">Endast för Fenny-redaktionen. Registrering är stängd — kontakta admin för åtkomst.</p>
        <label className="block space-y-1">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">E-post</span>
          <input
            type="email" required autoComplete="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Lösenord</span>
          <input
            type="password" required minLength={8} autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
          />
        </label>
        {err && <p className="text-sm text-destructive">{err}</p>}
        <button type="submit" disabled={busy}
          className="w-full rounded-full bg-mint px-4 py-2.5 text-sm font-medium text-mint-foreground disabled:opacity-60">
          {busy ? "Vänta…" : "Logga in"}
        </button>
      </form>
    </div>
  );
}
